import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './auth.service';
import { Observable } from 'rxjs';
import { registerLocaleData } from '@angular/common';
import { LoggerService } from './logger.service';
import { HumanizeDurationLanguage, HumanizeDuration, Language } from 'humanize-duration-ts';
import { Record } from '../models/record';
import { CustomerType } from '../models/client-configuration';

const EMPTY_FIELDS: Partial<Language> = {
  y: (t: number) => '',
  mo: (t: number) => '',
  w: (t: number) => '',
  m: (t: number) => '',
  s: (t: number) => '',
  ms: (t: number) => '',
  decimal: '',
};

@Injectable({
  providedIn: 'root',
})
export class LocaleService {
  readonly supportedLanguages = [
    'es',
    'en',
    'ca',
    'va',
    'eu',
    'gl',
    'fr',
    'pt-PT',
    'pt',
    'de',
    'nl',
    'zh',
    'tr',
    'uk',
    'ar',
    'he',
  ];

  private humanizeLangService: HumanizeDurationLanguage = new HumanizeDurationLanguage();
  private humanizer: HumanizeDuration = new HumanizeDuration(this.humanizeLangService);
  private customerType: CustomerType;
  language: string;

  setCustomerType(customerType: CustomerType) {
    this.customerType = customerType;
  }

  constructor(
    authService: AuthService,
    logger: LoggerService,
    private translate: TranslateService,
  ) {
    if (authService.isAuthenticated()) {
      this.language = authService.getLoggedUser().locale;
    } else {
      this.language = localStorage.getItem('language');
    }

    const searchParams = new URLSearchParams(window.location.search);
    const localeByQuery = searchParams.get('locale');

    if (localeByQuery) {
      this.language = localeByQuery;
      localStorage.setItem('language', localeByQuery);
    }

    this.language = this.language || translate.getBrowserLang();

    if (!this.supportedLanguages.includes(this.language)) {
      logger.warn(`"${this.language}" locale not supported, fallback to "en"`);
      this.language = 'en';
    }

    // Obtener el idioma real para el valenciano que usaremos para las cosas internas de Angular (pipes...)
    const realLanguage = this.language === 'va' ? 'ca-ES-valencia' : this.language;

    // Cargamos el archivo de traducciones para el idioma con ngx-translate (independiente de Angular)
    this.translate.use(this.language);
    logger.info('Resolved language:', this.language);

    // Registramos el idioma en Angular para poder usarlo en el app.module.ts con el InjectionToken LOCALE_ID
    import(
      /* webpackExclude: /\.d\.ts$/ */
      /* webpackMode: "lazy-once" */
      /* webpackChunkName: "i18n-extra" */
      `@/../node_modules/@angular/common/locales/${realLanguage}`
    ).then((lang) => registerLocaleData(lang.default));

    this.humanizer.addLanguage('eu', {
      y: (c: number) => {
        return c === 1 ? 'urtean' : 'urteak';
      },
      mo: (c: number) => {
        return c === 1 ? 'hilabetea' : 'hilabeteak';
      },
      w: (c: number) => {
        return c === 1 ? 'astean' : 'asteak';
      },
      d: (c: number) => {
        return c === 1 ? 'eguna' : 'egunak';
      },
      h: (c: number) => {
        return c === 1 ? 'ordua' : 'orduak';
      },
      m: (c: number) => {
        return c === 1 ? 'minutua' : 'minutu';
      },
      s: (c: number) => {
        return c === 1 ? 'bigarrena' : 'segundoak';
      },
      ms: (c: number) => {
        return c === 1 ? 'milisegundoa' : 'milisegundoak';
      },
      decimal: ","
    });

    this.humanizer.addLanguage('gl', {
      y: (c: number) => {
        return c === 1 ? 'ano' : 'anos';
      },
      mo: (c: number) => {
        return c === 1 ? 'mes' : 'meses';
      },
      w: (c: number) => {
        return c === 1 ? 'semana' : 'semanas';
      },
      d: (c: number) => {
        return c === 1 ? 'día' : 'días';
      },
      h: (c: number) => {
        return c === 1 ? 'hora' : 'horas';
      },
      m: (c: number) => {
        return c === 1 ? 'minuto' : 'minutos';
      },
      s: (c: number) => {
        return c === 1 ? 'segundo' : 'segundos';
      },
      ms: (c: number) => {
        return c === 1 ? 'milisegundo' : 'milisegundos';
      },
      decimal: ","
    });

    this.humanizer.addLanguage('he', {
      y: (c: number) => {
        return c === 1 ? "שנה" : "שנים";
      },
      mo: (c: number) => {
        return c === 1 ? "חודש" : "חודשים";
      },
      w: (c: number) => {
        return c === 1 ? "שבוע" : "שבועות";
      },
      d: (c: number) => {
        return c === 1 ? "יום" : "ימים";
      },
      h: (c: number) => {
        return c === 1 ? "שעה" : "שעות";
      },
      m: (c: number) => {
        return c === 1 ? "דקה" : "דקות";
      },
      s: (c: number) => {
        return c === 1 ? "שניה" : "שניות";
      },
      ms: (c: number) => {
        return c === 1 ? "מילישנייה" : "מילישניות";
      },
      decimal: ","
    });
  }

  getString(key: string | string[], params?: object): Observable<string> {
    return this.translate.get(key, params);
  }

  getStringBySolution(key: string, params?: object): Observable<string> {
    const keyBySolution = key + '.' + this.customerType.dbValue.toLowerCase();
    let result = this.getString(keyBySolution, params);
    result.subscribe((value) => {
      if (!value || value == keyBySolution) {
        result = this.getString(key, params);
      }
    });
    return result;
  }

  getLanguage() {
    return this.language;
  }

  instant(key: string | string[], params?: object): string {
    return this.translate.instant(key, params);
  }

  humanize(millis: number): string {
    const language = {
      'pt-PT': 'pt',
      'va': 'ca',
      'zh': 'zh_CN',
    }[this.language] ?? this.language;

    return this.humanizer.humanize(millis, {
      units: ['d', 'h'],
      delimiter: ' - ',
      round: true,
      language,
    });
  }

  getLocalizedShareText(record: Record, site: string): string {
    let formatKey: string;

    switch (record?.icon?.label) {
      case 'formats.newspaper':
      case 'formats.magazine':
      case 'formats.ebook':
      case 'formats.scorm':
      default:
        formatKey = 'user.share.reading';
        break;
      case 'formats.audio':
        formatKey = 'user.share.listening';
        break;
      case 'formats.video':
      case 'formats.image':
        formatKey = 'user.share.watching';
    }

    const action = this.translate.instant(formatKey);
    const title = record?.title;
    const by = this.translate.instant('general.by');
    const author = record?.author;
    const on = this.translate.instant('general.on');

    return `${action} ${title} ${by} ${author} ${on} ${site}`;
  }

  private addMissingTranslations(): void {
    this.humanizeLangService.addLanguage('eu', {
      d: (t: number) => 'eguna' + (t > 1 ? 'k' : ''),
      h: (t: number) => 'ordua' + (t > 1 ? 'k' : ''),
      ...EMPTY_FIELDS,
    } as Language);

    this.humanizeLangService.addLanguage('gl', {
      d: (t: number) => 'día' + (t > 1 ? 's' : ''),
      h: (t: number) => 'hora' + (t > 1 ? 's' : ''),
      ...EMPTY_FIELDS,
    } as Language);

    this.humanizeLangService.addLanguage('ca-ES-valencia', {
      d: (t: number) => 'día' + (t > 1 ? 's' : ''),
      h: (t: number) => 'hora' + (t > 1 ? 's' : ''),
      ...EMPTY_FIELDS,
    } as Language);

    this.humanizeLangService.addLanguage('zh', {
      d: () => '天',
      h: () => '小时',
      ...EMPTY_FIELDS,
    } as Language);

    this.humanizeLangService.addLanguage('he', {
      d: () => 'יְוֹם',
      h: () => 'שָׁעָה',
      ...EMPTY_FIELDS,
    } as Language);
  }

  getTextDirection(): string {
    if (this.language === 'ar' || this.language === 'he') {
      return 'rtl';
    }

    return 'ltr';
  }

  existsKey(key: string): boolean {
    return Boolean(this.translate.translations?.[this.language]?.[key]);
  }
}
